import { z } from 'zod'
import { CountryAlpha2 } from 'generated/graphql'
import {
  addressSchema,
  dateSchema,
  phoneNumberSchema,
  socialSecurityNumberSchema
} from 'utils/custom-schemas'

const signerBaseSchema = z.object({
  individual: z.object({
    firstName: z.string().min(1),
    middleName: z.string().nullish(),
    lastName: z.string().min(1),
    dateOfBirth: dateSchema(z.date()),
    nationalities: z.array(z.nativeEnum(CountryAlpha2)).min(1),
    ssn: z.string().nullish(),
    identificationDocumentType: z.string().nullish(),
    idNumber: z.string().nullish(),
    residentialAddress: addressSchema,
    email: z.string().min(1).email('invalidEmail'),
    phoneNumber: phoneNumberSchema({ message: 'invalidPhoneNumber' })
  }),
  relation: z.object({
    title: z.string().min(1)
  })
})

const signerCrossFieldRefinements = (
  data: unknown,
  ctx: z.RefinementCtx
): void => {
  const root = (data ?? {}) as {
    individual?: {
      nationalities?: string[] | null
      ssn?: string | null
      identificationDocumentType?: string | null
      idNumber?: string | null
    }
  }
  const isUs = root.individual?.nationalities?.includes(CountryAlpha2.Us)

  if (isUs) {
    const result = socialSecurityNumberSchema().safeParse(
      root.individual?.ssn?.trim() ?? ''
    )
    if (!result.success) {
      result.error.issues.forEach(issue =>
        ctx.addIssue({ ...issue, path: ['individual', 'ssn'] })
      )
    }
  } else {
    if (!root.individual?.identificationDocumentType?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['individual', 'identificationDocumentType']
      })
    }
    if (!root.individual?.idNumber?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['individual', 'idNumber']
      })
    }
  }
}

export const signerSchema = z.intersection(
  z.any().superRefine(signerCrossFieldRefinements),
  signerBaseSchema
)

export type Signer = z.infer<typeof signerBaseSchema>
